// Feature 08: Link Impact Correlation Chart

import { Box, Paper, Typography } from '@mui/material';
import { Info } from '@mui/icons-material';
import { CorrelationChartProps } from '../../types/linkImpact.types';
import LinkImpactMetricsTable from './LinkImpactMetricsTable';

const CorrelationChart: React.FC<CorrelationChartProps> = ({
  score,
  metricChanges,
}) => {
  const { value, confidenceInterval, pValue } = score;

  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Typography variant="h6" gutterBottom>
        Correlation Analysis
      </Typography>

      {/* Correlation Score Display */}
      <Box display="flex" alignItems="center" gap={2} mb={2}>
        <Box>
          <Typography variant="body2" color="text.secondary">
            Correlation Coefficient
          </Typography>
          <Typography variant="h4" color="primary">
            {value.toFixed(3)}
          </Typography>
        </Box>

        <Box>
          <Typography variant="body2" color="text.secondary">
            95% Confidence Interval
          </Typography>
          <Typography variant="body1">
            [{confidenceInterval[0].toFixed(3)}, {confidenceInterval[1].toFixed(3)}]
          </Typography>
        </Box>

        <Box>
          <Typography variant="body2" color="text.secondary">
            P-Value
          </Typography>
          <Typography variant="body1">
            {pValue < 0.001 ? '< 0.001' : pValue.toFixed(3)}
          </Typography>
        </Box>
      </Box>

      {/* Disclaimer */}
      <Box
        p={1.5}
        mb={2}
        bgcolor="info.lighter"
        borderRadius={1}
        border="1px solid"
        borderColor="info.light"
      >
        <Box display="flex" alignItems="flex-start" gap={1}>
          <Info color="info" fontSize="small" />
          <Typography variant="body2" color="info.dark">
            <strong>Supporting Signal:</strong> This shows correlation, not proven causation.
            Link acquisition timing correlates with performance changes, but other factors
            may contribute to observed outcomes.
          </Typography>
        </Box>
      </Box>

      {/* Metric Changes Table */}
      <LinkImpactMetricsTable metricChanges={metricChanges} />

      {/* Statistical Notes */}
      <Box mt={2}>
        <Typography variant="caption" color="text.secondary">
          <strong>Correlation Interpretation:</strong>
        </Typography>
        <Typography variant="caption" color="text.secondary" component="div">
          • {'|r|'} {'>'} 0.7: Strong correlation
        </Typography>
        <Typography variant="caption" color="text.secondary" component="div">
          • 0.4 {'≤'} {'|r|'} {'≤'} 0.7: Moderate correlation
        </Typography>
        <Typography variant="caption" color="text.secondary" component="div">
          • 0.1 {'≤'} {'|r|'} {'<'} 0.4: Weak correlation
        </Typography>
        <Typography variant="caption" color="text.secondary" component="div">
          • {'|r|'} {'<'} 0.1: Negligible correlation
        </Typography>
      </Box>
    </Paper>
  );
};

export default CorrelationChart;
